import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { 
  Save, 
  Loader2, 
  Video, 
  Globe, 
  User, 
  Share2, 
  Phone,
  RefreshCw
} from 'lucide-react'

interface ConfigItem {
  id: string
  value: string
  label: string | null
  category: string
  description: string | null
}

const categoryConfig = {
  hero: { icon: Video, title: 'Hero Section', description: 'Hero视频和背景配置' },
  general: { icon: Globe, title: 'General', description: '网站基本信息' },
  author: { icon: User, title: 'Author', description: '作者信息配置' },
  social: { icon: Share2, title: 'Social', description: '社交媒体链接' },
  contact: { icon: Phone, title: 'Contact', description: '联系方式配置' },
}

export function SiteConfigManager() {
  const [configs, setConfigs] = useState<ConfigItem[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [editedValues, setEditedValues] = useState<Record<string, string>>({})

  useEffect(() => {
    loadConfigs()
  }, [])

  const loadConfigs = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('site_config')
        .select('*')
        .order('category', { ascending: true })

      if (error) throw error
      setConfigs(data || [])
      
      // Initialize edited values
      const values: Record<string, string> = {}
      data?.forEach(item => {
        values[item.id] = item.value
      })
      setEditedValues(values)
    } catch (error: any) {
      console.error('Failed to load configs:', error)
      toast.error('Failed to load configurations')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (configId: string) => {
    const newValue = editedValues[configId]
    if (newValue === undefined) return

    setSaving(configId)
    try {
      const { error } = await supabase
        .from('site_config')
        .update({ value: newValue })
        .eq('id', configId)

      if (error) throw error
      
      // Update local state
      setConfigs(prev => prev.map(c => 
        c.id === configId ? { ...c, value: newValue } : c
      ))
      
      toast.success('Configuration saved')
    } catch (error: any) {
      console.error('Failed to save config:', error)
      toast.error(error.message || 'Failed to save configuration')
    } finally {
      setSaving(null)
    }
  }

  const handleSaveAll = async (category: string) => {
    const categoryConfigs = configs.filter(c => c.category === category)
    const changedConfigs = categoryConfigs.filter(c => editedValues[c.id] !== c.value)
    
    if (changedConfigs.length === 0) {
      toast.info('No changes to save')
      return
    }

    setSaving(category)
    try {
      for (const config of changedConfigs) {
        const { error } = await supabase
          .from('site_config')
          .update({ value: editedValues[config.id] })
          .eq('id', config.id)

        if (error) throw error
      }
      
      // Update local state
      setConfigs(prev => prev.map(c => 
        editedValues[c.id] !== undefined ? { ...c, value: editedValues[c.id] } : c
      ))
      
      toast.success(`Saved ${changedConfigs.length} configuration(s)`)
    } catch (error: any) {
      console.error('Failed to save configs:', error)
      toast.error(error.message || 'Failed to save configurations')
    } finally {
      setSaving(null)
    }
  }

  const hasChanges = (category: string) => {
    return configs
      .filter(c => c.category === category)
      .some(c => editedValues[c.id] !== c.value)
  }

  const getConfigsByCategory = (category: string) => {
    return configs.filter(c => c.category === category)
  }

  const renderConfigInput = (config: ConfigItem) => {
    const isMultiline = config.id.includes('bio') || config.id.includes('description')
    const value = editedValues[config.id] ?? config.value
    const hasChanged = value !== config.value
    
    return (
      <div key={config.id} className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={config.id} className="text-sm font-medium">
            {config.label || config.id}
            {hasChanged && <span className="ml-2 text-xs text-primary">*</span>}
          </Label>
          {hasChanged && (
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-6 text-xs"
              onClick={() => handleSave(config.id)}
              disabled={saving === config.id}
            >
              {saving === config.id ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
            </Button>
          )}
        </div>
        {isMultiline ? (
          <Textarea
            id={config.id}
            value={value}
            onChange={e => setEditedValues(prev => ({ ...prev, [config.id]: e.target.value }))}
            rows={3}
            className={hasChanged ? 'border-primary' : ''}
          />
        ) : (
          <Input
            id={config.id}
            value={value}
            onChange={e => setEditedValues(prev => ({ ...prev, [config.id]: e.target.value }))}
            className={hasChanged ? 'border-primary' : ''}
          />
        )}
        {config.description && (
          <p className="text-xs text-muted-foreground">{config.description}</p>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <div className="grid gap-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    )
  }

  const categories = Object.keys(categoryConfig) as Array<keyof typeof categoryConfig>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Site Configuration</h2>
          <p className="text-muted-foreground">管理网站配置，包括Hero视频、站点信息、社交链接等</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadConfigs}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="hero" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          {categories.map(category => {
            const config = categoryConfig[category]
            const Icon = config.icon
            return (
              <TabsTrigger key={category} value={category} className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{config.title}</span>
                {hasChanges(category) && (
                  <span className="w-2 h-2 rounded-full bg-primary" />
                )}
              </TabsTrigger>
            )
          })}
        </TabsList>

        {categories.map(category => {
          const config = categoryConfig[category]
          const Icon = config.icon
          const categoryConfigs = getConfigsByCategory(category)
          
          return (
            <TabsContent key={category} value={category}>
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-primary" />
                      <div>
                        <CardTitle>{config.title}</CardTitle>
                        <CardDescription>{config.description}</CardDescription>
                      </div>
                    </div>
                    <Button 
                      onClick={() => handleSaveAll(category)}
                      disabled={!hasChanges(category) || saving === category}
                    >
                      {saving === category ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {category === 'hero' && editedValues['hero_video_url'] && (
                    <div className="mb-6">
                      <Label className="text-sm font-medium mb-2 block">Preview</Label>
                      <video
                        src={editedValues['hero_video_url']}
                        className="w-full max-w-md h-40 object-cover rounded-lg border"
                        muted
                        loop
                        autoPlay
                        playsInline
                      />
                    </div>
                  )}
                  
                  <div className="grid gap-6 md:grid-cols-2">
                    {categoryConfigs.map(renderConfigInput)}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )
        })}
      </Tabs>
    </div>
  )
}
